import React, { useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';

const DeleteCandidateDialog = ({ open, onOpenChange, candidate, onDelete, isDeleting }) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (confirmText.toLowerCase() !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    try {
      setError(null);
      await onDelete(candidate.id);
      setConfirmText('');
      onOpenChange(false);
    } catch (err) {
      setError(err.message || 'Failed to delete candidate');
    }
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setConfirmText('');
      setError(null);
    }
    onOpenChange(newOpen);
  };

  const isConfirmValid = confirmText.toLowerCase() === 'delete';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>Delete Candidate</DialogTitle>
          </div>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the candidate record and all associated data.
          </DialogDescription>
        </DialogHeader>

        {candidate && (
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm font-medium">
                {candidate.first_name} {candidate.last_name}
              </p>
              {candidate.email && (
                <p className="text-sm text-muted-foreground">{candidate.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Type <span className="font-mono bg-muted px-1 rounded">delete</span> to confirm
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type 'delete' here"
                disabled={isDeleting}
                autoComplete="off"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmValid || isDeleting}
          >
            {isDeleting ? (
              <>Deleting...</>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Candidate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCandidateDialog;
